/* eslint-disable react/display-name */
import rowStyles from '@/components/StoreProductsTable/TableRow.module.css';
import { Modifier } from '@/types/menus.types';
import { FranchiseeModifierNamedPriceRow } from '@/types/namedPrice.types';
import { Box } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FZMenuModifierTable from './FZMenuModifierTable';
import styles from './FZStoreModifierItemTable.module.css';
import nameStyles from './FZNamedPriceModifierItemTable.module.css';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { StoreAndGroupType } from '@/context/context.types';
import { RowSelectionState } from '@tanstack/react-table';
import { formatModifierPriceCell } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import { mapNamedPriceRowsToModifierTableRows } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/namedPriceModifier.helpers';

export type FZNamedPriceModifierItemTableProps = {
  namedPriceRows: FranchiseeModifierNamedPriceRow[];
  dynamicHeaders: string[];
  isLoading: boolean;
  isFranchiseeEditAndFreeze?: boolean;
  selectedRows: RowSelectionState;
  onRowSelectionChange: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  changeHeaderSize: (size: string) => void;
  storesAndGroups: StoreAndGroupType;
  onOpenPriceDeviations: (namedPriceId: string, selectedGroupName: string) => void;
};

function getFirstEightChars(header: string): string {
  return header.substring(0, 8);
}

const getCellNamedPriceModifierName = () => {
  const CellNamedPriceModifierName = ({ row }: CellContext<Modifier, unknown>) => {
    const { modifierName } = row.original;
    return (
      <div className={nameStyles.modifierNameCell}>
        <span className={nameStyles.modifierName} data-testid="named-price-modifier-name">
          {modifierName}
        </span>
      </div>
    );
  };
  return CellNamedPriceModifierName;
};

const getNamedPriceDescription = (
  header: string,
  inactiveLabel: string,
  onOpenPriceDeviations: FZNamedPriceModifierItemTableProps['onOpenPriceDeviations'],
  rowIdByIndex: Map<number, string>,
) => {
  return ({ row }: CellContext<Modifier, unknown>) => {
    const { storeGroups } = row.original;
    const storeGroup = storeGroups.find((sg) => sg.storeGroupName === header);
    const { text, showRangeInfo } = formatModifierPriceCell(storeGroup?.chosenPriceRange, inactiveLabel);
    const namedPriceId = rowIdByIndex.get(Number(row.id)) ?? row.original.modifierId;

    return showRangeInfo ? (
      <div className={styles.rangePriceCell}>
        <div className={styles.noWrapPrice}>{text}</div>
        <InfoOutlined
          className={styles.priceRangeInfo}
          data-testid="named-price-range-info"
          sx={{
            width: '22px',
            height: '22px',
            color: '#266DCC',
            cursor: 'pointer',
          }}
          onClick={() => onOpenPriceDeviations(namedPriceId, header)}
        />
      </div>
    ) : (
      <span className={styles.noWrapPrice}>{text}</span>
    );
  };
};

const FZNamedPriceModifierItemTable = (props: FZNamedPriceModifierItemTableProps) => {
  const {
    changeHeaderSize,
    namedPriceRows,
    dynamicHeaders,
    selectedRows,
    onRowSelectionChange,
    isLoading,
    storesAndGroups,
    isFranchiseeEditAndFreeze,
    onOpenPriceDeviations,
  } = props;

  const { t } = useTranslation();

  const tableData = useMemo(() => mapNamedPriceRowsToModifierTableRows(namedPriceRows), [namedPriceRows]);

  const rowIdByIndex = useMemo(() => {
    const map = new Map<number, string>();
    namedPriceRows.forEach((row, index) => map.set(index, row.namedPriceId));
    return map;
  }, [namedPriceRows]);

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;
      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count as number;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length as number;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount as number;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length as number;
      }

      const headerWithFirstEightChars = getFirstEightChars(header);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <strong>{headerWithFirstEightChars}</strong>
          <span>
            {count} / {total}
          </span>
        </div>
      );
    },
    [storesAndGroups],
  );

  const columns = useMemo(() => {
    const inactiveLabel = t('common.inactive');
    const lColumns = [
      {
        header: t('menuDetails.storeItem.table.modifier'),
        accessorKey: 'modifierDescription',
        enableSorting: true,
        cell: getCellNamedPriceModifierName(),
        sortingFn: 'sortModifier',
        columnClassname: 'productHeaderStyle',
      } as any,
    ];
    dynamicHeaders.forEach((header, index) => {
      lColumns.push({
        header: getStoreGroupsCount(header),
        accessorKey: header,
        columnClassname:
          index < dynamicHeaders.length - 1
            ? dynamicHeaders.length < 6
              ? 'dynamicHeader150'
              : 'dynamicHeader'
            : 'dynamicHeaderLastItem',
        enableSorting: true,
        cell: getNamedPriceDescription(header, inactiveLabel, onOpenPriceDeviations, rowIdByIndex),
        sortingFn: 'sortModifierByPrice',
      } as any);
    });

    return lColumns;
  }, [t, dynamicHeaders, getStoreGroupsCount, onOpenPriceDeviations, rowIdByIndex]);

  return (
    <div className={rowStyles.tablePage} data-testid="named-price-modifier-table">
      <div>
        <Box>
          <FZMenuModifierTable
            returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
            data={tableData}
            isLoading={isLoading}
            enableRowSelection
            columns={columns}
            selectedRows={selectedRows}
            onRowSelectionChange={onRowSelectionChange}
            changeHeaderSize={changeHeaderSize}
            isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
          />
        </Box>
      </div>
    </div>
  );
};

export default FZNamedPriceModifierItemTable;
